import { z } from 'zod';
import { EmailSchema, TimestampSchema, UuidSchema } from './primitives';

export const PromotionRewardTypeSchema = z.enum(['cash', 'credits', 'cv_processing']);
export type PromotionRewardType = z.infer<typeof PromotionRewardTypeSchema>;

export const PromotionStatusSchema = z.enum(['in_progress', 'completed', 'expired', 'cancelled']);
export type PromotionStatus = z.infer<typeof PromotionStatusSchema>;

export const InvitationStatusSchema = z.enum([
  'pending',
  'sent',
  'opened',
  'clicked',
  'registered',
  'expired',
  'cancelled',
]);
export type InvitationStatus = z.infer<typeof InvitationStatusSchema>;

export const AiStatusSchema = z.enum(['pending', 'passed', 'failed', 'review']);
export type AiStatus = z.infer<typeof AiStatusSchema>;

export const PayoutTypeSchema = z.enum(['bank_transfer', 'service_credit']);
export type PayoutType = z.infer<typeof PayoutTypeSchema>;

export const NotificationTypeSchema = z.enum([
  'reward_received',
  'invitation_accepted',
  'invitation_pending',
  'invitation_expired',
  'iban_required',
  'transfer_completed',
  'promotion_completed',
  'promotion_expiring',
  'system_announcement',
]);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

export const PromotionSchema = z.object({
  created_at: TimestampSchema,
  description_en: z.string().nullable(),
  description_ka: z.string().nullable(),
  duration_days: z.number().int().positive(),
  ends_at: TimestampSchema.nullable(),
  icon: z.string().nullable(),
  id: UuidSchema,
  is_active: z.boolean(),
  required_count: z.number().int().positive(),
  reward_amount: z.coerce.number().positive(),
  reward_type: PromotionRewardTypeSchema,
  slug: z.string().min(1),
  starts_at: TimestampSchema.nullable(),
  title_en: z.string().min(1),
  title_ka: z.string().min(1),
});
export type Promotion = z.infer<typeof PromotionSchema>;

export const UserPromotionSchema = z.object({
  change_count: z.number().int().nonnegative(),
  completed_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
  expires_at: TimestampSchema,
  id: UuidSchema,
  promotion_id: UuidSchema,
  reward_amount: z.coerce.number().nullable(),
  reward_paid: z.boolean(),
  started_at: TimestampSchema,
  status: PromotionStatusSchema,
  tenant_id: UuidSchema,
  user_id: UuidSchema,
  promotions: PromotionSchema.optional(),
});
export type UserPromotion = z.infer<typeof UserPromotionSchema>;

export const AiAnalysisResultSchema = z.object({
  confidence_score: z.number().int().min(0).max(100),
  details: z.record(z.unknown()).optional().default({}),
  is_valid_hr: z.boolean(),
  issues: z.array(z.string()).default([]),
  risk_score: z.number().int().min(0).max(100),
});
export type AiAnalysisResult = z.infer<typeof AiAnalysisResultSchema>;

export const PromoInvitationSchema = z.object({
  ai_analysis_result: z.record(z.unknown()).nullable(),
  ai_confidence_score: z.number().int().min(0).max(100).nullable(),
  ai_risk_score: z.number().int().min(0).max(100).nullable(),
  ai_status: AiStatusSchema,
  clicked_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
  deleted_at: TimestampSchema.nullable(),
  expires_at: TimestampSchema.nullable(),
  id: UuidSchema,
  invitee_email: EmailSchema,
  invitee_first_name: z.string().min(1),
  invitee_last_name: z.string().min(1),
  invitee_linkedin_url: z.string().url().nullable(),
  inviter_id: UuidSchema,
  opened_at: TimestampSchema.nullable(),
  registered_at: TimestampSchema.nullable(),
  registered_user_id: UuidSchema.nullable(),
  sent_at: TimestampSchema.nullable(),
  status: InvitationStatusSchema,
  tenant_id: UuidSchema,
  unique_token: z.string().min(16),
  user_promotion_id: UuidSchema,
});
export type PromoInvitation = z.infer<typeof PromoInvitationSchema>;

export const InvitationInputSchema = z.object({
  ai_analysis_result: AiAnalysisResultSchema.optional(),
  invitee_email: EmailSchema.transform((email) => email.toLowerCase()),
  invitee_first_name: z.string().min(1).max(120),
  invitee_last_name: z.string().min(1).max(120),
  invitee_linkedin_url: z.string().url().optional().nullable(),
  screenshot_paths: z.array(z.string().min(1).max(512)).max(5).optional().default([]),
});
export type InvitationInput = z.input<typeof InvitationInputSchema>;

export const InvitationScreenshotSchema = z.object({
  delete_after: TimestampSchema,
  file_size_bytes: z.number().int().nonnegative().nullable(),
  id: UuidSchema,
  invitation_id: UuidSchema,
  storage_path: z.string().min(1),
  tenant_id: UuidSchema,
  uploaded_at: TimestampSchema,
});
export type InvitationScreenshot = z.infer<typeof InvitationScreenshotSchema>;

export const DeviceFingerprintSchema = z.object({
  created_at: TimestampSchema,
  fingerprint_data: z.record(z.unknown()).nullable(),
  id: UuidSchema,
  ip_address: z.string().nullable(),
  ip_country: z.string().nullable(),
  ip_isp: z.string().nullable(),
  is_proxy: z.boolean(),
  is_tor: z.boolean(),
  is_vpn: z.boolean(),
  risk_score: z.number().int().min(0).max(100),
  user_id: UuidSchema.nullable(),
  visitor_id: z.string().min(1),
});
export type DeviceFingerprint = z.infer<typeof DeviceFingerprintSchema>;

export const HrUserBalanceSchema = z.object({
  earned_promotions: z.coerce.number(),
  iban: z.string().nullable(),
  iban_bank_name: z.string().nullable(),
  iban_holder_name: z.string().nullable(),
  iban_verified: z.boolean(),
  pending_balance: z.coerce.number(),
  total_balance: z.coerce.number(),
  updated_at: TimestampSchema,
  user_id: UuidSchema,
  withdrawn_total: z.coerce.number(),
});
export type HrUserBalance = z.infer<typeof HrUserBalanceSchema>;

export const HrTransactionSchema = z.object({
  amount: z.coerce.number().positive(),
  balance_after: z.coerce.number().nonnegative(),
  created_at: TimestampSchema,
  description: z.string().nullable(),
  id: UuidSchema,
  reference_id: UuidSchema.nullable(),
  reference_type: z
    .enum(['promotion_reward', 'cv_work', 'withdrawal', 'service_purchase', 'admin_adjust'])
    .nullable(),
  status: z.enum(['pending', 'completed', 'failed']),
  type: z.enum(['credit', 'debit']),
  user_id: UuidSchema,
});
export type HrTransaction = z.infer<typeof HrTransactionSchema>;

export const HrPayoutRequestSchema = z.object({
  admin_note: z.string().nullable(),
  amount: z.coerce.number().positive(),
  bog_reference_id: z.string().nullable(),
  created_at: TimestampSchema,
  iban: z.string().nullable(),
  iban_bank_name: z.string().nullable(),
  id: UuidSchema,
  payout_type: PayoutTypeSchema,
  processed_at: TimestampSchema.nullable(),
  status: z.enum(['pending', 'processing', 'completed', 'failed', 'cancelled']),
  user_id: UuidSchema,
});
export type HrPayoutRequest = z.infer<typeof HrPayoutRequestSchema>;

export const HrNotificationSchema = z.object({
  action_url: z.string().nullable(),
  body_en: z.string().nullable(),
  body_ka: z.string().nullable(),
  created_at: TimestampSchema,
  id: UuidSchema,
  is_read: z.boolean(),
  metadata: z.record(z.unknown()).nullable(),
  read_at: TimestampSchema.nullable(),
  title_en: z.string().min(1),
  title_ka: z.string().min(1),
  type: NotificationTypeSchema,
  user_id: UuidSchema,
});
export type HrNotification = z.infer<typeof HrNotificationSchema>;

export const PromotionProgressSchema = z.object({
  completed_count: z.number().int().nonnegative(),
  invitees: z.array(PromoInvitationSchema),
  required_count: z.number().int().positive(),
  user_promotion: UserPromotionSchema,
});
export type PromotionProgress = z.infer<typeof PromotionProgressSchema>;

export const IbanInputSchema = z.object({
  iban: z.string().regex(/^GE\d{2}[A-Z]{2}\d{16}$/),
  iban_bank_name: z.string().min(1).max(160),
  iban_holder_name: z.string().min(1).max(200),
});
export type IbanInput = z.infer<typeof IbanInputSchema>;

export const PayoutRequestSchema = z.object({
  amount: z.number().positive(),
  payout_type: PayoutTypeSchema,
});
export type PayoutRequest = z.infer<typeof PayoutRequestSchema>;

export const NotifFilterSchema = z
  .object({
    limit: z.number().int().positive().max(100).optional(),
    unread_only: z.boolean().optional(),
    type: NotificationTypeSchema.optional(),
  })
  .partial();
export type NotifFilter = z.infer<typeof NotifFilterSchema>;
